/**
 * State machine for the Generator / Unit / Summative form (`GeneratorForm.jsx`).
 *
 * **Phase 96 (Feb 2026) — multi-subject / multi-strand / multi-expectation.**
 *
 * Teachers can now build cross-curricular lessons (Math + Science), pick
 * multiple strands across the chosen subject(s), and tick multiple
 * specific expectations spanning those strands. Caps:
 *   - Single lesson: max 4 expectations
 *   - Unit (N lessons): max max(4, min(12, N*2)) expectations
 *   - Summative: 2-8 expectations
 *
 * Canonical state (the source of truth):
 *   - `subjects[]`     : ordered subject names (primary first)
 *   - `strands[]`      : ordered { subject, code } pairs
 *   - `expectations[]` : ordered { subject, strand, code } triples
 *
 * Legacy `form.subject`, `form.strand`, `form.expectation`, and
 * `summativeExpectations` remain as DERIVED getters for backward
 * compatibility with URL prefills, sessionStorage prefills, Math
 * Engine → Generator handoff, and the existing summative validation.
 * They are kept in lock-step via the reducer so no consumer needs
 * to switch.
 */
import { SUBJECT_OPTIONS } from "./options";

// Pure dictionary lookups over the curriculum payload. Inlined here
// (vs imported from ./curriculum.js) so this module stays free of
// React / axios / network dependencies and can be Jest-tested.
// Expand a combined grade ("Grade 5/6") into its component single-grade
// keys. Single grades pass through unchanged.
const _expandGrades = (grade) => {
  const nums = String(grade || "").match(/[4-8]/g) || [];
  const uniq = [...new Set(nums)];
  if (uniq.length <= 1) return [grade];
  return uniq.map((n) => `Grade ${n}`);
};
const _subjectsForGrade = (c, grade) => {
  const out = [];
  for (const g of _expandGrades(grade)) {
    for (const s of (c?.subjects_by_grade?.[g] || [])) if (!out.includes(s)) out.push(s);
  }
  return out;
};
const _strandCodesForGradeSubject = (c, grade, subject) => {
  const out = [];
  for (const g of _expandGrades(grade)) {
    for (const s of ((c?.strands?.[g] || {})[subject] || [])) if (!out.includes(s.code)) out.push(s.code);
  }
  return out;
};

export const INITIAL_FORM_FIELDS = {
  skill_focus: "",
  grade: "",
  subject: "",
  strand: "",
  expectation: "",
  // Tier 2 Polish (#3): sensible defaults so teachers only have to
  // actively choose Skill, Grade, and Subject. Both remain fully
  // changeable in the dropdowns.
  time: "",
  activity_type: "Hands-on / Maker",
  num_lessons: "5",
  prompt: "",
  // Reading Generator (Feb 2026) — opt-in, lesson output type only.
  include_reading: false,
  reading_text_type: "",
  // Phase 107.L — Support mode delivers a shorter reading (150-350
  // words) plus a 2-question multiple-choice Section A. Defaults to
  // "anchor" so existing teacher flows are unchanged.
  reading_mode: "anchor",
  // Jun 2026 — multi-level readings. Teacher can tick 1, 2, or all 3
  // levels; each becomes its own downloadable Student Reading sheet.
  reading_modes: ["anchor"],
};

export const INITIAL_FORM_STATE = {
  mode: "lesson",
  form: INITIAL_FORM_FIELDS,
  // Canonical multi-pick lists.
  subjects: [],
  strands: [],
  expectations: [],
  // Legacy summative single-strand checklist. Mirrors expectations[]
  // where {subject,strand} match the primary subject + primary strand.
  summativeExpectations: [],
  skillSubcomponents: [],
};

// ----------------------------------------------------------------
// Sync helpers — derive legacy single-pick fields from canonical
// arrays AFTER every state transition so the rest of the codebase
// (validation, payload building, prefills, etc.) keeps working.
// ----------------------------------------------------------------

const _sync = (state) => {
  const primarySubject = state.subjects[0] || "";
  const primaryStrand =
    state.strands.find((s) => s.subject === primarySubject)?.code || "";

  // Only re-derive `summativeExpectations` when the canonical pickers
  // are actually in use (we have a primary subject + strand). If the
  // teacher is still using the legacy single-pick form fields and the
  // canonical `expectations[]` is empty, leave `summativeExpectations`
  // untouched so seed-state callers keep their values.
  const canonicalInUse = !!primarySubject && !!primaryStrand;
  const summativeExpectations = canonicalInUse
    ? state.expectations
        .filter((e) => e.subject === primarySubject && e.strand === primaryStrand)
        .map((e) => e.code)
    : state.summativeExpectations;

  // Same idea for `form.subject` / `form.strand` — only overwrite if
  // we have a canonical pick. Otherwise preserve the legacy values.
  const nextSubject = primarySubject || state.form.subject;
  const nextStrand = primaryStrand || (primarySubject ? "" : state.form.strand);

  return {
    ...state,
    form: {
      ...state.form,
      subject: nextSubject,
      strand: nextStrand,
    },
    summativeExpectations,
  };
};

// ----------------------------------------------------------------
// Pruning — drop subjects/strands/expectations that are no longer
// valid against the curriculum (e.g. when the teacher changes grade).
// ----------------------------------------------------------------

const _pruneAgainstCurriculum = (state, curriculum) => {
  if (!curriculum) return state;
  const grade = state.form.grade || "";
  const validSubjects = new Set(
    grade ? _subjectsForGrade(curriculum, grade) : SUBJECT_OPTIONS
  );

  const filteredSubjects = state.subjects.filter((s) => validSubjects.has(s));
  const subjectsChanged = filteredSubjects.length !== state.subjects.length;
  const subjects = subjectsChanged ? filteredSubjects : state.subjects;

  const validStrandsBySubject = {};
  for (const subj of subjects) {
    validStrandsBySubject[subj] = new Set(
      _strandCodesForGradeSubject(curriculum, grade, subj)
    );
  }
  const filteredStrands = state.strands.filter(
    (s) =>
      subjects.includes(s.subject) &&
      validStrandsBySubject[s.subject]?.has(s.code)
  );
  const strandsChanged = filteredStrands.length !== state.strands.length;
  const strands = strandsChanged ? filteredStrands : state.strands;

  const allowedExpKey = new Set(strands.map((s) => `${s.subject}|${s.code}`));
  const filteredExps = state.expectations.filter((e) =>
    allowedExpKey.has(`${e.subject}|${e.strand}`)
  );
  const expsChanged = filteredExps.length !== state.expectations.length;
  const expectations = expsChanged ? filteredExps : state.expectations;

  if (!subjectsChanged && !strandsChanged && !expsChanged) {
    return state;
  }
  return { ...state, subjects, strands, expectations };
};

// If the caller hands us a state where legacy `form.subject` /
// `form.strand` is filled but the canonical `subjects[]` / `strands[]`
// arrays are empty (e.g. INITIAL_FORM_STATE after a URL prefill landed
// only in form.*), auto-promote the legacy values up so prune + sync
// can do their job and downstream UI doesn't see a split brain.
const _autoPromoteLegacyFormFields = (state) => {
  const { form } = state;
  let subjects = state.subjects;
  let strands = state.strands;
  let expectations = state.expectations;

  if (form.subject && !subjects.includes(form.subject)) {
    subjects = [form.subject, ...subjects];
  }
  if (
    form.subject &&
    form.strand &&
    !strands.some((s) => s.subject === form.subject && s.code === form.strand)
  ) {
    strands = [{ subject: form.subject, code: form.strand }, ...strands];
  }
  if (
    form.subject &&
    form.strand &&
    form.expectation &&
    !expectations.some(
      (e) =>
        e.subject === form.subject &&
        e.strand === form.strand &&
        e.code === form.expectation
    )
  ) {
    expectations = [
      { subject: form.subject, strand: form.strand, code: form.expectation },
      ...expectations,
    ];
  }
  if (
    subjects === state.subjects &&
    strands === state.strands &&
    expectations === state.expectations
  ) {
    return state;
  }
  return { ...state, subjects, strands, expectations };
};
// Public alias — kept for callers that imported it.
export const reconcileWithCurriculum = (form, summativeExpectations, curriculum) => {
  // Legacy-shape adapter. Wraps the modern pruner around the old
  // single-pick (form.subject/strand) inputs callers expect. We
  // synthesise the canonical arrays, prune them, then translate
  // back to (form, summativeExpectations) keeping form.expectation
  // alongside (never collapsed into expectations[]).
  const synthetic = {
    form,
    subjects: form.subject ? [form.subject] : [],
    strands: form.strand && form.subject ? [{ subject: form.subject, code: form.strand }] : [],
    expectations: (summativeExpectations || []).map((code) => ({
      subject: form.subject || "",
      strand: form.strand || "",
      code,
    })),
  };
  const pruned = _pruneAgainstCurriculum(synthetic, curriculum);
  // If nothing was pruned, return the inputs by-reference so callers
  // that compare references (e.g. RECONCILE_AGAINST_CURRICULUM short-
  // circuit) keep working.
  const sameSubject = pruned.subjects.length === synthetic.subjects.length;
  const sameStrand = pruned.strands.length === synthetic.strands.length;
  const sameExps = pruned.expectations.length === synthetic.expectations.length;
  if (sameSubject && sameStrand && sameExps) {
    return { form, summativeExpectations };
  }
  // Rebuild form by stripping any pruned fields. expectation field
  // travels with strand (when strand drops, expectation drops too).
  const nextSubject = pruned.subjects[0] || "";
  const nextStrand = pruned.strands[0]?.code || "";
  const nextForm = {
    ...form,
    subject: nextSubject,
    strand: nextStrand,
    expectation: nextStrand ? form.expectation : "",
  };
  const nextSummative = pruned.expectations.map((e) => e.code);
  return { form: nextForm, summativeExpectations: nextSummative };
};

// ----------------------------------------------------------------
// Reducer
// ----------------------------------------------------------------

export function formReducer(state, action) {
  switch (action.type) {
    case "SET_MODE": {
      if (state.mode === action.mode) return state;
      // Mode swap keeps subjects/strands but wipes expectations + the
      // legacy summativeExpectations mirror — the teacher will re-pick
      // based on new caps + summative needing 2+.
      return _sync({
        ...state,
        mode: action.mode,
        expectations: [],
        summativeExpectations: [],
      });
    }

    case "SET_FIELD": {
      const { key, value, curriculum } = action;
      if (key === "skill_focus") {
        return _sync({
          ...state,
          form: { ...state.form, skill_focus: value },
          skillSubcomponents: [],
        });
      }
      // Auto-promote legacy form fields into canonical arrays before
      // any cascading field change, so older test seeds and existing
      // sessionStorage prefills (which only fill form.*) cascade
      // correctly through the new prune logic.
      const promoted = _autoPromoteLegacyFormFields(state);
      // Grade change → wipe subjects/strands/expectations + summative
      // codes (different curated curricula per grade), then re-prune.
      if (key === "grade") {
        const next = {
          ...promoted,
          form: { ...promoted.form, grade: value },
          expectations: [],
          summativeExpectations: [],
        };
        const pruned = _pruneAgainstCurriculum(next, curriculum);
        // If the legacy primary subject/strand survived prune they're
        // still on pruned.subjects[]. If they didn't survive, also
        // clear the legacy form.subject/strand/expectation fields so
        // they don't leak into the new grade's form state.
        if (curriculum) {
          const primary = pruned.subjects[0] || "";
          const primaryStrand =
            pruned.strands.find((s) => s.subject === primary)?.code || "";
          const legacySubjectInvalid =
            promoted.form.subject && primary !== promoted.form.subject;
          const legacyStrandInvalid =
            promoted.form.strand && primaryStrand !== promoted.form.strand;
          if (legacySubjectInvalid || legacyStrandInvalid) {
            const cleared = { ...pruned.form };
            if (legacySubjectInvalid) cleared.subject = "";
            if (legacySubjectInvalid || legacyStrandInvalid) cleared.strand = "";
            if (legacySubjectInvalid || legacyStrandInvalid) cleared.expectation = "";
            return _sync({ ...pruned, form: cleared });
          }
        }
        return _sync(pruned);
      }
      // Subject change via legacy single-select setter — replaces the
      // PRIMARY subject (subjects[0]) and prunes downstream picks that
      // depended on the old primary subject.
      if (key === "subject") {
        const oldPrimary = promoted.subjects[0] || "";
        let subjects;
        if (!value) {
          subjects = promoted.subjects.slice(1); // wipe primary, keep others
        } else if (oldPrimary === value) {
          subjects = promoted.subjects.slice();
        } else {
          // Replace primary, dedupe, prepend if not already in list.
          subjects = [value, ...promoted.subjects.filter((s) => s !== value && s !== oldPrimary)];
        }
        const strands = promoted.strands.filter((s) => subjects.includes(s.subject));
        const allowedKey = new Set(strands.map((s) => `${s.subject}|${s.code}`));
        const expectations = promoted.expectations.filter((e) =>
          allowedKey.has(`${e.subject}|${e.strand}`)
        );
        const next = {
          ...promoted,
          subjects,
          strands,
          expectations,
          summativeExpectations: [],
        };
        return _sync(_pruneAgainstCurriculum(next, curriculum));
      }
      // Strand change via legacy single-select setter — replaces the
      // primary strand for the primary subject.
      if (key === "strand") {
        const primary = promoted.subjects[0] || "";
        if (!primary) return state;
        const otherStrands = promoted.strands.filter((s) => s.subject !== primary);
        let strands;
        if (!value) {
          strands = otherStrands;
        } else {
          strands = [
            { subject: primary, code: value },
            ...otherStrands,
            ...promoted.strands.filter(
              (s) => s.subject === primary && s.code !== value
            ),
          ];
        }
        const allowedKey = new Set(strands.map((s) => `${s.subject}|${s.code}`));
        const expectations = promoted.expectations.filter((e) =>
          allowedKey.has(`${e.subject}|${e.strand}`)
        );
        const next = {
          ...promoted,
          strands,
          expectations,
          // Strand change wipes the strand-scoped legacy fields.
          form: { ...promoted.form, expectation: "" },
          summativeExpectations: [],
        };
        return _sync(_pruneAgainstCurriculum(next, curriculum));
      }
      // Expectation single-pick (lesson/unit non-summative legacy).
      // Treats it as a one-shot REPLACE of the primary-subject/primary-
      // strand expectation slot.
      if (key === "expectation") {
        const primary = state.subjects[0] || "";
        const primaryStrand =
          state.strands.find((s) => s.subject === primary)?.code || "";
        if (!primary || !primaryStrand) {
          return _sync({ ...state, form: { ...state.form, expectation: value } });
        }
        // Drop any existing exp on (primary, primaryStrand) then add
        // the new one (if non-empty).
        const filtered = state.expectations.filter(
          (e) => !(e.subject === primary && e.strand === primaryStrand)
        );
        const expectations = value
          ? [{ subject: primary, strand: primaryStrand, code: value }, ...filtered]
          : filtered;
        // Mirror the pick onto the legacy `form.expectation` so the
        // single-pick <Select> (bound to form.expectation) reflects the
        // chosen code instead of falling back to its placeholder.
        return _sync({
          ...state,
          expectations,
          form: { ...state.form, expectation: value },
        });
      }
      return _sync({ ...state, form: { ...state.form, [key]: value } });
    }

    case "ADD_SUBJECT": {
      const { subject } = action;
      if (!subject || state.subjects.includes(subject)) return state;
      return _sync({ ...state, subjects: [...state.subjects, subject] });
    }

    case "REMOVE_SUBJECT": {
      const { subject } = action;
      if (!state.subjects.includes(subject)) return state;
      const subjects = state.subjects.filter((s) => s !== subject);
      const strands = state.strands.filter((s) => s.subject !== subject);
      const expectations = state.expectations.filter((e) => e.subject !== subject);
      return _sync({ ...state, subjects, strands, expectations });
    }

    case "TOGGLE_STRAND": {
      const { subject, code } = action;
      if (!subject || !code) return state;
      const has = state.strands.some(
        (s) => s.subject === subject && s.code === code
      );
      let strands;
      let expectations = state.expectations;
      if (has) {
        strands = state.strands.filter(
          (s) => !(s.subject === subject && s.code === code)
        );
        expectations = state.expectations.filter(
          (e) => !(e.subject === subject && e.strand === code)
        );
      } else {
        strands = [...state.strands, { subject, code }];
      }
      return _sync({ ...state, strands, expectations });
    }

    case "TOGGLE_EXPECTATION": {
      const { subject, strand, code, grade } = action;
      if (!subject || !strand || !code) return state;
      const g = grade || "";
      const eq = (e) =>
        e.subject === subject && e.strand === strand && e.code === code && (e.grade || "") === g;
      const has = state.expectations.some(eq);
      let expectations;
      if (has) {
        expectations = state.expectations.filter((e) => !eq(e));
      } else {
        expectations = [
          ...state.expectations,
          grade ? { subject, strand, code, grade } : { subject, strand, code },
        ];
      }
      return _sync({ ...state, expectations });
    }

    case "PRESELECT_EXPECTATIONS": {
      // 1b — the big-idea picker pre-checks the server-computed
      // `suggested_specific_codes` (or, for a teacher-written idea, the
      // strand-wide fallback). Replaces expectations[] wholesale; the
      // teacher then adjusts freely. Client does NO matching of its own.
      const list = (action.expectations || []).filter(
        (e) => e && e.subject && e.strand && e.code
      );
      // Register every strand referenced by a pre-selected expectation in
      // `strands[]`. A big idea can suggest specifics spanning multiple strands
      // (B, C, …) while the form's primary strand is one value. Without this,
      // the next curriculum reconcile (`_pruneAgainstCurriculum`, which keeps an
      // expectation only if its strand is in the SELECTED strands) silently
      // drops every cross-strand pre-selection — emptying inputs.expectations
      // and producing the honest-zero. (Jul 2026 capture-bug fix.)
      const strands = [...state.strands];
      for (const e of list) {
        if (!strands.some((s) => s.subject === e.subject && s.code === e.strand)) {
          strands.push({ subject: e.subject, code: e.strand });
        }
      }
      return _sync({ ...state, strands, expectations: list });
    }

    case "TOGGLE_SUMMATIVE_EXPECTATION": {
      // Toggle the code on the legacy `summativeExpectations` array
      // first (so consumers who only seeded that array keep working).
      // If we also have a primary subject + primary strand we keep
      // the canonical `expectations[]` array in lock-step.
      const { code } = action;
      const hasLegacy = state.summativeExpectations.includes(code);
      const nextLegacy = hasLegacy
        ? state.summativeExpectations.filter((c) => c !== code)
        : [...state.summativeExpectations, code];

      const primary = state.subjects[0] || state.form.subject || "";
      const primaryStrand =
        state.strands.find((s) => s.subject === primary)?.code ||
        state.form.strand ||
        "";

      let expectations = state.expectations;
      if (primary && primaryStrand) {
        const hasCanonical = state.expectations.some(
          (e) =>
            e.subject === primary &&
            e.strand === primaryStrand &&
            e.code === code
        );
        if (hasCanonical) {
          expectations = state.expectations.filter(
            (e) =>
              !(
                e.subject === primary &&
                e.strand === primaryStrand &&
                e.code === code
              )
          );
        } else {
          expectations = [
            ...state.expectations,
            { subject: primary, strand: primaryStrand, code },
          ];
        }
      }
      return {
        ...state,
        expectations,
        summativeExpectations: nextLegacy,
      };
    }

    case "TOGGLE_SKILL_SUBCOMPONENT": {
      const { name } = action;
      const has = state.skillSubcomponents.includes(name);
      return {
        ...state,
        skillSubcomponents: has
          ? state.skillSubcomponents.filter((n) => n !== name)
          : [...state.skillSubcomponents, name],
      };
    }

    case "APPLY_URL_PREFILL": {
      const { mode, form: formPatch, expectations, curriculum } = action;
      const nextForm = { ...state.form, ...(formPatch || {}) };
      // Auto-promote single-pick prefill into canonical arrays.
      const subjects = nextForm.subject ? [nextForm.subject] : state.subjects;
      const strands =
        nextForm.subject && nextForm.strand
          ? [{ subject: nextForm.subject, code: nextForm.strand }]
          : state.strands;
      let nextExps = state.expectations;
      if (expectations && expectations.length) {
        // Treat legacy `expectations` payload as belonging to the
        // primary subject + primary strand.
        const subj = nextForm.subject || "";
        const strn = nextForm.strand || "";
        nextExps = expectations.map((code) => ({
          subject: subj,
          strand: strn,
          code,
        }));
      } else if (nextForm.expectation && nextForm.subject && nextForm.strand) {
        nextExps = [
          { subject: nextForm.subject, strand: nextForm.strand, code: nextForm.expectation },
        ];
      }
      const merged = {
        ...state,
        mode: mode && ["lesson", "unit", "summative"].includes(mode) ? mode : state.mode,
        form: nextForm,
        subjects,
        strands,
        expectations: nextExps,
      };
      return _sync(_pruneAgainstCurriculum(merged, curriculum));
    }

    case "APPLY_SESSION_PREFILL": {
      const { prompt, skillFocus } = action;
      const nextForm = { ...state.form };
      if (prompt) nextForm.prompt = prompt;
      if (skillFocus) nextForm.skill_focus = skillFocus;
      return _sync({
        ...state,
        mode: prompt ? "lesson" : state.mode,
        form: nextForm,
        skillSubcomponents: skillFocus ? [] : state.skillSubcomponents,
      });
    }

    case "RECONCILE_AGAINST_CURRICULUM": {
      const { curriculum } = action;
      if (!curriculum) return state;
      // Two-track reconcile:
      //   1. Drop legacy form.subject / strand / expectation if invalid
      //      (same behaviour the test suite has relied on since Phase 90b).
      //   2. Drop entries from the canonical arrays whose (grade,
      //      subject, strand) combination no longer fits curriculum.
      // Fast-path: if neither needs touching, return state by-reference.
      const grade = state.form.grade || "";
      const validSubjects = new Set(
        grade ? _subjectsForGrade(curriculum, grade) : SUBJECT_OPTIONS
      );
      const subjectInvalid =
        state.form.subject && !validSubjects.has(state.form.subject);
      const strandValid =
        state.form.subject && state.form.strand
          ? new Set(
              _strandCodesForGradeSubject(curriculum, grade, state.form.subject)
            ).has(state.form.strand)
          : true;
      const strandInvalid = !!state.form.strand && !strandValid;

      const pruned = _pruneAgainstCurriculum(state, curriculum);
      if (!subjectInvalid && !strandInvalid && pruned === state) {
        return state;
      }
      let nextForm = state.form;
      if (subjectInvalid) {
        nextForm = { ...nextForm, subject: "", strand: "", expectation: "" };
      } else if (strandInvalid) {
        nextForm = { ...nextForm, strand: "", expectation: "" };
      }
      const nextState =
        nextForm === state.form && pruned === state
          ? state
          : { ...pruned, form: nextForm };
      // summativeExpectations needs re-derivation if (subject, strand)
      // changed AND the canonical arrays still have entries — but for
      // legacy callers passing the pure-form state, we manually wipe
      // when the legacy strand was dropped.
      if (subjectInvalid || strandInvalid) {
        const primary = nextState.subjects[0] || "";
        const primaryStrand =
          nextState.strands.find((s) => s.subject === primary)?.code || "";
        return {
          ...nextState,
          summativeExpectations: nextState.expectations
            .filter(
              (e) => e.subject === primary && e.strand === primaryStrand
            )
            .map((e) => e.code),
        };
      }
      return _sync(nextState);
    }

    default:
      return state;
  }
}
